import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { DashboardCourseCard } from "@/components/dashboard/DashboardCourseCard";
import { ConfirmDialog } from "@/components/dashboard/ConfirmDialog";
import { AppTopbar } from "@/components/layout/AppTopbar";
import { Sidebar } from "@/components/layout/Sidebar";
import { usePlaylistIdParam } from "@/hooks/useUrlParams";
import {
  usePlaylistItem,
  usePlaylistCoursesForPlaylist,
  useDeletePlaylist,
  useUpdatePlaylistOrder,
  useRemoveCourseFromPlaylist,
  useUpdatePlaylistMusicUrl,
  useUpdatePlaylistCourseNotes,
  useUpdatePlaylistName,
} from "@/hooks/usePlaylists";
import {
  useCoursePermissions,
  useUserCourseProgress,
  getCourseProgress,
  hasPermission,
  getCourseProgressWidth,
} from "@/hooks/usePermissions";
import {
  useCreatePlaylistShare,
  useCreateCourseShare,
} from "@/hooks/useSharing";
import { useStripeRenewal } from "@/hooks/useCheckout";
import { useAuth } from "@/lib/auth";
import { buildSharePlaylistUrl, buildShareTutorialUrl, copyToClipboard } from "@/lib/helpers";
import { useAppStore } from "@/lib/store";
import type { PlaylistCourseWithDetails, CoursePermissionWithCourse } from "@/lib/schema";
import { CommentSection } from "@/components/comments/CommentSection";
import { MusicMixSection } from "@/components/playlist/MusicMixSection";
import { CompleteRoutineSection } from "@/components/playlist/CompleteRoutineSection";

export function PlaylistPage() {
  const playlistId = usePlaylistIdParam();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: playlist, loading: playlistLoading, setData: setPlaylist, refetch: refetchPlaylist } =
    usePlaylistItem(playlistId);
  const { data: playlistCourses, loading: coursesLoading, refetch: refetchCourses, setData: setPlaylistCourses } =
    usePlaylistCoursesForPlaylist(playlistId);
  const { data: permissions } = useCoursePermissions();
  const { data: progress } = useUserCourseProgress();
  const { execute: createPlaylistShare } = useCreatePlaylistShare();
  const { execute: createCourseShare } = useCreateCourseShare();
  const { execute: deletePlaylist } = useDeletePlaylist();
  const { execute: updatePlaylistOrder, loading: savingOrder } = useUpdatePlaylistOrder();
  const { execute: removeCourseFromPlaylist } = useRemoveCourseFromPlaylist();
  const { execute: updateMusicUrl } = useUpdatePlaylistMusicUrl();
  const { execute: updateCourseNotes } = useUpdatePlaylistCourseNotes();
  const { execute: updatePlaylistName, loading: renameLoading } = useUpdatePlaylistName();
  const { execute: stripeRenewal } = useStripeRenewal();
  const [extendingPermissionId, setExtendingPermissionId] = useState<string | null>(null);
  const { dashboardSearchInput, setDashboardSearchInput } = useAppStore();

  const defaultInstructorImage =
    "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

  const filteredCourses = playlistCourses ?? [];

  const [copiedShare, setCopiedShare] = useState(false);
  const [isRearranging, setIsRearranging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reorderedCourses, setReorderedCourses] = useState<PlaylistCourseWithDetails[] | null>(null);
  const [removeCourseTarget, setRemoveCourseTarget] = useState<PlaylistCourseWithDetails | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [editingPlaylistName, setEditingPlaylistName] = useState(false);
  const [playlistNameDraft, setPlaylistNameDraft] = useState("");
  const [playlistNameError, setPlaylistNameError] = useState<string | null>(null);
  const playlistNameEditorRef = useRef<HTMLDivElement>(null);

  // When entering rearrange mode, snapshot the current order
  const displayCourses = isRearranging && reorderedCourses ? reorderedCourses : filteredCourses;

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleShare = async () => {
    if (!playlistId || !user) return;

    // Always go through the mutation so an existing share's expiry is refreshed
    // before copying the URL. Copying a cached active URL can hand clients a
    // link that is only hours/days away from expiry.
    const result = await createPlaylistShare(playlistId);
    const invitationToken = result.data?.invitation_token;
    if (!invitationToken) return;

    const copied = await copyToClipboard(buildSharePlaylistUrl(invitationToken));
    if (copied) {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };

  const handleDelete = async () => {
    if (!playlistId) return;
    setDeleteLoading(true);
    await deletePlaylist(playlistId);
    setDeleteLoading(false);
    setShowDeleteConfirm(false);
    navigate("/app/dashboard/home#performances");
  };

  const startEditingPlaylistName = () => {
    if (!playlist) return;
    setPlaylistNameDraft(playlist.name);
    setPlaylistNameError(null);
    setEditingPlaylistName(true);
  };

  const cancelEditingPlaylistName = () => {
    setEditingPlaylistName(false);
    setPlaylistNameDraft("");
    setPlaylistNameError(null);
  };

  const handleRenamePlaylist = async () => {
    if (!playlistId || !playlist || renameLoading) return;

    const trimmed = playlistNameDraft.trim();
    if (!trimmed) {
      setPlaylistNameError("Performance name is required.");
      return;
    }

    if (trimmed === playlist.name) {
      cancelEditingPlaylistName();
      return;
    }

    setPlaylistNameError(null);
    const result = await updatePlaylistName(playlistId, trimmed);
    if (result.error) {
      setPlaylistNameError(result.error);
      return;
    }

    const nextName = result.data?.name ?? trimmed;
    setPlaylist((prev) => (prev ? { ...prev, name: nextName } : prev));
    window.dispatchEvent(
      new CustomEvent("bollyrhythm:playlist-renamed", {
        detail: { id: playlistId, name: nextName },
      }),
    );
    cancelEditingPlaylistName();
  };

  useEffect(() => {
    if (!editingPlaylistName) return;

    function handlePointerDown(event: PointerEvent) {
      const editor = playlistNameEditorRef.current;
      const target = event.target;
      if (!editor || !(target instanceof Node) || editor.contains(target) || renameLoading) return;

      if (!playlistNameDraft.trim()) {
        cancelEditingPlaylistName();
        return;
      }

      void handleRenamePlaylist();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [editingPlaylistName, playlistNameDraft, renameLoading, handleRenamePlaylist]);

  const handleRemoveCourse = async () => {
    if (!removeCourseTarget) return;
    setRemoveLoading(true);
    const result = await removeCourseFromPlaylist(removeCourseTarget.id);
    setRemoveLoading(false);
    if (result.error) {
      console.error("Failed to remove course:", result.error);
      setRemoveCourseTarget(null);
      return;
    }
    // Optimistically remove from local state
    setPlaylistCourses((prev) =>
      prev ? prev.filter((pc) => pc.id !== removeCourseTarget.id) : prev,
    );
    setRemoveCourseTarget(null);
  };

  const handleStartRearranging = () => {
    setReorderedCourses([...filteredCourses]);
    setIsRearranging(true);
  };

  const handleCancelRearranging = () => {
    setReorderedCourses(null);
    setIsRearranging(false);
  };

  const handleSaveOrder = async () => {
    if (!playlistId || !reorderedCourses) return;
    const courseIds = reorderedCourses.map((pc) => pc.course_id.id);
    await updatePlaylistOrder(playlistId, courseIds);
    setReorderedCourses(null);
    setIsRearranging(false);
    refetchCourses();
  };

  const handleMove = (idx: number, direction: "up" | "down") => {
    if (!reorderedCourses) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= reorderedCourses.length) return;
    const updated = [...reorderedCourses];
    [updated[idx], updated[targetIdx]] = [updated[targetIdx]!, updated[idx]!];
    setReorderedCourses(updated);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !reorderedCourses) return;

    const oldIndex = reorderedCourses.findIndex((pc) => pc.id === active.id);
    const newIndex = reorderedCourses.findIndex((pc) => pc.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const updated = [...reorderedCourses];
    const [moved] = updated.splice(oldIndex, 1) as [PlaylistCourseWithDetails];
    updated.splice(newIndex, 0, moved);
    setReorderedCourses(updated);
  };

  const handleCourseShare = async (courseId: string) => {
    if (!user) return false;
    // Always go through the mutation so an existing tutorial share's expiry is
    // refreshed before copying the URL.
    const result = await createCourseShare(courseId);
    const shareToken = result.data?.share_token;
    if (!shareToken) return false;
    return copyToClipboard(buildShareTutorialUrl(shareToken));
  };

  const toPermission = (pc: PlaylistCourseWithDetails): CoursePermissionWithCourse => {
    const real = permissions?.find((p) => p.course_id.id === pc.course_id.id);
    if (real) return real;
    return {
      id: pc.id,
      user_id: user?.id ?? "",
      expires_at: null,
      course_id: {
        id: pc.course_id.id,
        name: pc.course_id.name,
        price: pc.course_id.price,
        thumbnail_links_course: pc.course_id.thumbnail_links_course,
        preview_video_id: pc.course_id.preview_video_id,
        Instructor: pc.course_id.Instructor,
      },
    };
  };

  const loading = playlistLoading || coursesLoading;

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b] min-[992px]:flex-row">
      <Sidebar />

      <div className="min-w-0 flex-1">
        <AppTopbar
          title={playlist?.name ?? "Performance"}
          subtitle={`${filteredCourses.length} Video${filteredCourses.length === 1 ? "" : "s"}`}
          backTo="/app/dashboard/home#performances"
          searchValue={dashboardSearchInput}
          onSearchChange={setDashboardSearchInput}
        />

        {/* shell_inner-wrapper */}
        <div className="h-[calc(100vh-4.5rem)] overflow-auto max-[767px]:h-[calc(100vh-4rem)]">
          <div className="px-[clamp(1rem,3vw,3rem)]">
            <div className="py-12 max-[991px]:py-10 max-[767px]:py-6">
              {loading ? (
                <div className="space-y-4">
                  <div className="h-16 animate-[app-skeleton-pulse_1.8s_ease-in-out_infinite] rounded-[1rem] bg-[linear-gradient(180deg,#ffffff_0%,#f4f2f4_100%)]" />
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-24 animate-[app-skeleton-pulse_1.8s_ease-in-out_infinite] rounded-[1rem] bg-[linear-gradient(180deg,#ffffff_0%,#f4f2f4_100%)]"
                    />
                  ))}
                </div>
              ) : playlist ? (
                <>
                  {/* Playlist header */}
                  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      {editingPlaylistName ? (
                        <div ref={playlistNameEditorRef} className="max-w-[42rem]">
                          <input
                            type="text"
                            value={playlistNameDraft}
                            aria-label="Performance name"
                            className="w-full rounded-[0.5rem] border border-[#762a5c] bg-white px-3 py-2 font-[family-name:var(--font-display)] text-[clamp(1.5rem,1.2143rem+1.4286vw,2.5rem)] font-medium leading-[1.2] text-[#321b29] outline-none ring-2 ring-[#762a5c]/15 disabled:opacity-60"
                            autoFocus
                            disabled={renameLoading}
                            onChange={(e) => setPlaylistNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                void handleRenamePlaylist();
                              }
                              if (e.key === "Escape") {
                                e.preventDefault();
                                cancelEditingPlaylistName();
                              }
                            }}
                          />
                          <p className="mt-2 text-[0.8125rem] text-[#70696f]">
                            {renameLoading ? "Saving..." : "Press Enter to save, Escape to cancel."}
                          </p>
                          {playlistNameError && (
                            <p className="mt-1 text-[0.875rem] text-[#ba4d4d]">{playlistNameError}</p>
                          )}
                        </div>
                      ) : (
                        <h1 className="m-0 font-[family-name:var(--font-display)] text-[clamp(1.5rem,1.2143rem+1.4286vw,2.5rem)] font-medium leading-[1.2] text-[#321b29]">
                          <button
                            type="button"
                            className="block max-w-full truncate rounded-[0.35rem] text-left font-[inherit] text-inherit transition-colors hover:text-[#762a5c] focus:outline-none focus:ring-2 focus:ring-[#762a5c]/30"
                            aria-label="Edit performance name"
                            title="Click to edit performance name"
                            onClick={startEditingPlaylistName}
                          >
                            {playlist.name}
                          </button>
                        </h1>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {!isRearranging && (
                        <>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-[0.5rem] border border-[#e2e4e9] bg-white px-5 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
                            onClick={() => setShowDeleteConfirm(true)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 256 256"
                              fill="currentColor"
                            >
                              <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z" />
                            </svg>
                            Delete Performance
                          </button>

                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-[0.5rem] border border-[#e2e4e9] bg-white px-5 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
                            onClick={() => void handleShare()}
                          >
                            {copiedShare ? (
                              <>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 256 256"
                                  fill="currentColor"
                                >
                                  <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" />
                                </svg>
                                Link Copied!
                              </>
                            ) : (
                              <>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 256 256"
                                  fill="currentColor"
                                >
                                  <path d="M212,200a36,36,0,1,1-69.85-12.19l-53.3-30.75a36,36,0,1,1,0-58.12l53.3-30.75a36.09,36.09,0,1,1,8,13.86l-53.3,30.75a36.15,36.15,0,0,1,0,30.4l53.3,30.75A36,36,0,0,1,212,200Z" />
                                </svg>
                                Share Performance
                              </>
                            )}
                          </button>
                        </>
                      )}

                      {filteredCourses.length > 0 && (isRearranging ? (
                        <>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-[0.5rem] border border-[#e2e4e9] bg-white px-5 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
                            onClick={handleCancelRearranging}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-[0.5rem] border border-[#762a5c] bg-[#762a5c] px-5 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-white transition-transform duration-200 hover:-translate-y-[5%] active:scale-95 disabled:opacity-60"
                            onClick={() => void handleSaveOrder()}
                            disabled={savingOrder}
                          >
                            {savingOrder ? "Saving..." : "Save Order"}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center gap-2 rounded-[0.5rem] border border-[#e2e4e9] bg-white px-5 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
                          onClick={handleStartRearranging}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 256 256"
                            fill="currentColor"
                          >
                            <path d="M40,128a8,8,0,0,1,8-8H208a8,8,0,0,1,0,16H48A8,8,0,0,1,40,128Zm8-56H208a8,8,0,0,0,0-16H48a8,8,0,0,0,0,16Zm160,112H48a8,8,0,0,0,0,16H208a8,8,0,0,0,0-16Z" />
                          </svg>
                          Rearrange
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Course list */}
                  {displayCourses.length > 0 ? (
                    isRearranging ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={displayCourses.map((pc) => pc.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="grid gap-[0.85rem]">
                            {displayCourses.map((pc, idx) => (
                              <SortableCourseItem
                                key={pc.id}
                                pc={pc}
                                idx={idx}
                                total={displayCourses.length}
                                onMove={handleMove}
                                user={user}
                                permissions={permissions}
                                progress={progress}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    ) : (
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(26rem,100%),1fr))] gap-[1.75rem]">
                        {displayCourses.map((pc, idx) => (
                          <DashboardCourseCard
                            key={pc.id}
                            permission={toPermission(pc)}
                            instructorImage={defaultInstructorImage}
                            performanceHref="/app/dashboard/home#performances"
                            playlistId={playlistId}
                            sequenceNumber={idx + 1}
                            onShare={handleCourseShare}
                            onExtend={(permissionId) => {
                              setExtendingPermissionId(permissionId);
                              void stripeRenewal(permissionId).finally(() => setExtendingPermissionId(null));
                            }}
                            extending={extendingPermissionId === toPermission(pc).id}
                            onRemove={() => setRemoveCourseTarget(pc)}
                            progressPercent={
                              progress && user
                                ? getCourseProgress(progress, pc.course_id.id, user.id)
                                : 0
                            }
                            notes={pc.notes}
                            onNoteSave={async (notes) => {
                              const previousNotes = pc.notes;

                              setPlaylistCourses((prev) =>
                                prev
                                  ? prev.map((item) =>
                                      item.id === pc.id ? { ...item, notes } : item,
                                    )
                                  : prev,
                              );

                              const result = await updateCourseNotes(pc.id, notes);
                              if (result.error) {
                                setPlaylistCourses((prev) =>
                                  prev
                                    ? prev.map((item) =>
                                        item.id === pc.id
                                          ? { ...item, notes: previousNotes }
                                          : item,
                                      )
                                    : prev,
                                );
                                refetchCourses();
                                return result.error;
                              }

                              refetchCourses();
                              return null;
                            }}
                          />
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="rounded-[1rem] border border-[#e2e4e9] bg-white p-[1.35rem]">
                      <h3 className="m-0 font-[family-name:var(--font-display)] text-[2rem] leading-[1.04] text-[#321b29] max-sm:text-[1.8rem]">
                        No courses yet
                      </h3>
                      <p className="mt-[0.7rem] text-[0.96rem] text-[#70696f]">
                        Browse the library to add tutorials to this performance.
                      </p>
                      <Link
                        to="/dance-library"
                        className="mt-4 inline-flex items-center justify-center gap-2 rounded-[0.5rem] bg-[#762a5c] px-6 py-3 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-white transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
                      >
                        Browse Library
                      </Link>
                    </div>
                  )}

                  <CompleteRoutineSection playlistId={playlistId!} canGenerate suspended={isRearranging} className="mt-8 max-w-[38rem]" />
                  {!isRearranging && (
                    <>
                    <MusicMixSection
                      playlistId={playlistId!}
                      musicUrl={playlist.music_url ?? null}
                      onMusicUrlChange={async (url) => {
                        const previousMusicUrl = playlist.music_url ?? null;

                        setPlaylist((prev) =>
                          prev ? { ...prev, music_url: url } : prev,
                        );

                        const result = await updateMusicUrl(playlistId!, url);
                        if (result.error) {
                          setPlaylist((prev) =>
                            prev ? { ...prev, music_url: previousMusicUrl } : prev,
                          );
                          refetchPlaylist();
                          return result.error;
                        }

                        refetchPlaylist();
                        return null;
                      }}
                      className="mt-8 max-w-[38rem]"
                    />
                    <CommentSection
                      playlistId={playlistId}
                      organizerUserId={playlist.user_id ?? null}
                      className="mt-8 max-w-[38rem]"
                    />
                    </>
                  )}
                </>
              ) : (
                <div className="rounded-[1rem] border border-[#e2e4e9] bg-white p-[1.35rem]">
                  <h3 className="m-0 font-[family-name:var(--font-display)] text-[2rem] leading-[1.04] text-[#321b29] max-sm:text-[1.8rem]">
                    Performance not found
                  </h3>
                  <p className="mt-[0.7rem] text-[0.96rem] text-[#70696f]">
                    This performance could not be found or may have been
                    deleted.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Performance"
        description={`Are you sure you want to delete "${playlist?.name ?? "this performance"}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmLabel="Delete Performance"

        loading={deleteLoading}
        onConfirm={() => void handleDelete()}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmDialog
        open={removeCourseTarget !== null}
        title="Remove Tutorial"
        description="Remove this tutorial from your performance?"
        confirmLabel="Remove"
        loading={removeLoading}
        onConfirm={() => void handleRemoveCourse()}
        onCancel={() => setRemoveCourseTarget(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable course item for drag-and-drop
// ---------------------------------------------------------------------------

function SortableCourseItem({
  pc,
  idx,
  total,
  onMove,
  user,
  permissions,
  progress,
}: {
  pc: PlaylistCourseWithDetails;
  idx: number;
  total: number;
  onMove: (idx: number, direction: "up" | "down") => void;
  user: { id: string } | null;
  permissions: import("@/lib/schema").CoursePermissionWithCourse[] | null;
  progress: import("@/lib/schema").UserCourseProgressRow[] | null;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const courseId = pc.course_id.id;
  const courseName = pc.course_id.name;
  const owned =
    user && permissions ? hasPermission(permissions, courseId, user.id) : false;
  const progressWidth =
    progress && user
      ? getCourseProgressWidth(progress, courseId, user.id)
      : "0%";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 rounded-[1rem] border border-[#e2e4e9] bg-white px-5 py-4"
    >
      {/* Drag handle (desktop) */}
      <button
        type="button"
        className="hidden min-[992px]:flex h-10 w-10 shrink-0 cursor-grab items-center justify-center rounded-full bg-[#762a5c]/10 text-[#762a5c] active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor">
          <path d="M104,60A12,12,0,1,1,92,48,12,12,0,0,1,104,60Zm60-12a12,12,0,1,0,12,12A12,12,0,0,0,164,48ZM92,116a12,12,0,1,0,12,12A12,12,0,0,0,92,116Zm72,0a12,12,0,1,0,12,12A12,12,0,0,0,164,116ZM92,184a12,12,0,1,0,12,12A12,12,0,0,0,92,184Zm72,0a12,12,0,1,0,12,12A12,12,0,0,0,164,184Z" />
        </svg>
      </button>

      {/* Up/Down buttons (mobile) */}
      <div className="flex min-[992px]:hidden flex-col gap-1 shrink-0">
        {idx > 0 && (
          <button
            type="button"
            onClick={() => onMove(idx, "up")}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#762a5c]/10 text-[#762a5c]"
          >
            <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor">
              <path d="M213.66,165.66a8,8,0,0,1-11.32,0L128,91.31,53.66,165.66a8,8,0,0,1-11.32-11.32l80-80a8,8,0,0,1,11.32,0l80,80A8,8,0,0,1,213.66,165.66Z" />
            </svg>
          </button>
        )}
        {idx < total - 1 && (
          <button
            type="button"
            onClick={() => onMove(idx, "down")}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#762a5c]/10 text-[#762a5c]"
          >
            <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor">
              <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80a8,8,0,0,1,11.32-11.32L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1">
        <h3 className="m-0 text-[1.08rem] font-semibold text-[#271f24]">
          {courseName}
        </h3>
        {owned && (
          <div className="mt-1 h-1.5 w-32 overflow-hidden rounded-full bg-[#f0edef]">
            <div
              className="h-full rounded-full bg-[#762a5c] transition-all"
              style={{ width: progressWidth }}
            />
          </div>
        )}
        {!owned && (
          <p className="mt-0.5 text-[0.875rem] text-[#7f777d]">
            Not yet purchased
          </p>
        )}
      </div>

      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 font-[family-name:var(--font-display)] text-[1rem] font-semibold text-[#762a5c]">
        {idx + 1}
      </div>
    </div>
  );
}
